import { ROLE_LABELS } from '@/lib/portalUsers';
import {
  BILLING_CYCLES,
  GEOGRAPHIC_SCOPES,
  INVOICE_STATUSES,
  ORGANIZATION_TYPES,
  PAYMENT_STATUSES,
  STATUS_LABELS,
  formatDate,
  formatDay,
  labelFor,
} from '@/lib/organization';

/**
 * A logged value, before or after a change, as text a person can read.
 *
 * The backend stores whatever the column held: text, numbers, booleans, dates,
 * the three contacts as small objects, the states and links as lists. Secrets
 * never reach the log, and images are recorded only as "present" or null, so
 * everything here is safe to show as it is.
 */

/** Said for a value that is not there. */
export const EMPTY = 'Empty';

const DAY = /^\d{4}-\d{2}-\d{2}$/;
const MOMENT = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/;

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isFlat(value: Record<string, unknown>): boolean {
  return Object.values(value).every(
    (part) => part === null || ['string', 'number', 'boolean'].includes(typeof part),
  );
}

/** One scalar, as text. */
function scalar(value: unknown): string {
  if (value === null || value === undefined || value === '') return EMPTY;
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'string') {
    // A plain day is read as that day, not as midnight somewhere else.
    if (DAY.test(value)) return formatDay(value);
    if (MOMENT.test(value)) return formatDate(value);
    return value;
  }
  // Money arrives as text already ("1500.00"), so a number here is a count.
  if (typeof value === 'number' || typeof value === 'bigint') return String(value);
  // Only reached for something nested inside a flat check that let it through.
  return JSON.stringify(value);
}

/** What `formatValue` gives: a line of text, or several, or raw JSON. */
export type FormattedValue =
  | { kind: 'text'; text: string }
  | { kind: 'lines'; lines: string[] }
  | { kind: 'json'; json: string };

/**
 * The words for a stored code, for the fields whose values are codes. A status
 * reads "Suspended", not "SUSPENDED"; an unknown code is shown as it came.
 */
const CODE_WORDS: Record<string, (code: string) => string> = {
  status: (code) => STATUS_LABELS[code] ?? code,
  role: (code) => (ROLE_LABELS as Record<string, string>)[code] ?? code,
  organization_type: (code) => labelFor(ORGANIZATION_TYPES, code),
  geographic_scope: (code) => labelFor(GEOGRAPHIC_SCOPES, code),
  plan_billing_cycle: (code) => labelFor(BILLING_CYCLES, code),
  invoice_status: (code) => labelFor(INVOICE_STATUSES, code),
  payment_status: (code) => labelFor(PAYMENT_STATUSES, code),
};

/**
 * Format one value. Pass the field it belongs to, so a code in a field that
 * holds codes is shown in words.
 */
export function formatValue(value: unknown, field?: string): FormattedValue {
  const words = field ? CODE_WORDS[field] : undefined;
  if (words && typeof value === 'string' && value !== '') {
    return { kind: 'text', text: words(value) };
  }
  if (Array.isArray(value)) {
    if (value.length === 0) return { kind: 'text', text: EMPTY };
    if (value.every((item) => !isPlainObject(item) && !Array.isArray(item))) {
      return { kind: 'text', text: value.map(scalar).join(', ') };
    }
    return { kind: 'json', json: JSON.stringify(value, null, 2) };
  }

  if (isPlainObject(value)) {
    const entries = Object.entries(value).filter(
      ([, part]) => part !== null && part !== '',
    );
    if (entries.length === 0) return { kind: 'text', text: EMPTY };
    // A contact reads as "name: Dana Reed" and so on, one part a line.
    if (isFlat(value)) {
      return {
        kind: 'lines',
        lines: entries.map(([key, part]) => `${partLabel(key)}: ${scalar(part)}`),
      };
    }
    return { kind: 'json', json: JSON.stringify(value, null, 2) };
  }

  return { kind: 'text', text: scalar(value) };
}

/** The same, flattened to one string, for a place with room for a line only. */
export function formatValueText(value: unknown, field?: string): string {
  const formatted = formatValue(value, field);
  if (formatted.kind === 'text') return formatted.text;
  if (formatted.kind === 'lines') return formatted.lines.join('; ');
  return formatted.json;
}

/**
 * Column names in words. The log records the backend's own field names; the
 * ones a person is likely to see are here, and anything else is shown as it
 * came, with the underscores made spaces.
 */
const FIELD_LABELS: Record<string, string> = {
  name: 'Name',
  slug: 'Address name',
  description: 'Description',
  organization_type: 'Type',
  website: 'Website',
  logo: 'Logo',
  logo_url: 'Logo',
  cover_image: 'Cover image',
  cover_image_url: 'Cover image',
  social_links: 'Social links',
  geographic_scope: 'Geographic scope',
  geographic_states: 'States',
  scope_description: 'Scope description',
  primary_contact: 'Primary contact',
  public_contact: 'Public contact',
  billing_contact: 'Billing contact',
  status: 'Status',
  status_reason: 'Status reason',
  plan_name: 'Plan',
  plan_cost: 'Cost',
  plan_currency: 'Currency',
  plan_billing_cycle: 'Billing cycle',
  contract_start_date: 'Contract start',
  contract_end_date: 'Contract end',
  renewal_date: 'Renewal date',
  invoice_status: 'Invoice status',
  payment_status: 'Payment status',
  last_invoice_number: 'Last invoice number',
  billing_notes: 'Billing notes',
  monthly_notification_limit: 'Monthly notification allowance',
  role: 'Role',
  email: 'Email address',
  full_name: 'Name',
  // A record's own title - the ARC's. A contact's is a job title; see below.
  title: 'Title',
  phone: 'Phone',
  address: 'Address',
};

/** The parts of a contact whose words differ from the same name on a record. */
const CONTACT_PART_LABELS: Record<string, string> = {
  title: 'Job title',
};

export function fieldLabel(field: string): string {
  return FIELD_LABELS[field] ?? field.replace(/_/g, ' ');
}

/** One part of a flat value, which in the log is always a contact. */
function partLabel(part: string): string {
  return CONTACT_PART_LABELS[part] ?? fieldLabel(part);
}

/**
 * Fields an organization's own people must never be shown, even in the log.
 *
 * Staff's internal billing notes and invoice bookkeeping, the status reason in
 * staff's own wording, and the allowance override, which the organization sees
 * only as the limit it produces. The backend has left all four out of the
 * organization's feed since 23 September (`WITHHELD_FROM_ORGANIZATION` in
 * `audit_service.py`); the organization's views drop them too, so a feed from a
 * backend that forgot would still not show them, in the drawer or in a row's
 * sentence.
 */
export const STAFF_ONLY_FIELDS: ReadonlySet<string> = new Set([
  'billing_notes',
  'last_invoice_number',
  'status_reason',
  'monthly_notification_limit',
]);
